import { filter, flatMap, isArray, isEmpty, isObject, size } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { CloseRounded } from "@mui/icons-material";
import { Box, Card } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo } from "react";
import { formatLoanName } from "../../_src_shared/utils/formatters/formatLoanName";
import { htmlStringToText } from "../../_src_shared/utils/functions/htmlStringToText";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import CellRendererPriority from "../../components/agGrid/cellRenderer/CellRendererPriority";
import Flx from "../../components/layout/Flx";
import { DASHBOARD_SIDEBAR_TYPES } from "../../components/sidebars/DashboardSidebar/DashboardSidebar";
import LoanConditionsTable from "../../components/tables/LoanConditionsTable";
import Txt from "../../components/typography/Txt";
import { sidebarOpen } from "../../redux/reducers/sidebarReducer";

const ActiveFinancingRequestsConditionsSection = ({ mt = 8 }) => {
  const financingRequests = useSelector((state) => state?.financingRequests);

  const dispatch = useDispatch();

  const conditions = useMemo(() => {
    return getPendingUnderwritingRequirements(financingRequests);
  }, [financingRequests]);

  const filteredLoansWithConditions = useMemo(() => {
    return financingRequests?.filter((fr) => {
      return (
        isArray(fr?.underwritingRequirements) &&
        !isEmpty(fr?.underwritingRequirements)
      );
    });
  }, [financingRequests]);

  const hasAnyConditions = useMemo(() => size(conditions) > 0, [conditions]);
  const onRowClicked = useCallback((params, loanId) => {
    dispatch(
      sidebarOpen(DASHBOARD_SIDEBAR_TYPES.UPDATE_LOAN_CONDITION, params?.data, {
        loanId,
        conditionId: params.data._id,
      }),
    );
  }, []);
  if (!hasAnyConditions) {
    return null;
  }

  return (
    <Card
      sx={{
        p: 2.5,
        // width: "1000%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "10px",
        // boxShadow: open
        boxShadow: "none",
        background: "#fdeded",
        border: `1px solid ${red[100]}`,
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        borderTop: "none",
        // boxShadow: open
        //   ? "0 15px 40px rgba(0,0,0,0.25)"
        //   : "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      {/* <AllBorrowerConditionsTable conditions={allConditionItems} /> */}
      <Flx column g={4}>
        {filteredLoansWithConditions?.map((fr) => {
          return (
            <Flx column g={0.5}>
              <Txt bold>{fr.loanName}</Txt>
              <LoanConditionsTable
                rowData={getPendingUnderwritingRequirements(
                  fr.underwritingRequirements,
                )}
                onRowClicked={(d) => onRowClicked(d, fr?._id)}
              />
            </Flx>
          );
        })}
      </Flx>
      {/* <LoanConditionsTable rowData={conditions} onRowClicked={onRowClicked} /> */}
    </Card>
  );
};

const getPendingUnderwritingRequirements = (financingRequest) => {
  if (!isObject(financingRequest)) return [];

  return (
    financingRequest?.filter(
      (req) =>
        req?.status !== "Accepted" &&
        req?.docStipStatus !== "Received - Pending Review" &&
        req?.docStipStatus !== "Accepted",
    ) || []
  );
};

const getPendingUnderwritingRequirementsAll = (financingRequests) => {
  if (!isArray(financingRequests)) return [];

  return filter(
    flatMap(financingRequests, (loan) =>
      isArray(loan?.underwritingRequirements)
        ? loan.underwritingRequirements
        : [],
    ),
    (req) =>
      req?.status !== "Accepted" &&
      req?.docStipStatus !== "Received - Pending Review" &&
      req?.docStipStatus !== "Accepted",
  );
};

const AllBorrowerConditionsTable = ({ conditions = [] }) => {
  const dispatch = useDispatch();
  console.log("conditions", conditions);
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Loan",
        rowGroup: true,
        hide: true,
        valueGetter: (p) => formatLoanName(p.data?.loanData) || "N/A",
        minWidth: 250,
        // flex: 1,
      },

      {
        headerName: "Details",

        minWidth: 100,
        flex: 1,
        cellStyle: {
          paddingTop: "14px",
          paddingBottom: "14px",
        },
        wrapText: true,
        autoHeight: true,
        cellRenderer: (params) => (
          <CellRendererRequirementDetails params={params} />
        ),
      },
      {
        headerName: "Status",
        width: 130,
        flex: 0,
        cellStyle: {
          paddingTop: "8px",
          // paddingBottom: "14px",
        },
        cellRenderer: (params) => (
          <CellRendererPriority
            includePrefix
            value={params.data?.data?.priority || "Normal"}
          />
        ),
      },

      {
        width: 38,
        headerName: "",
        pinned: "right",
        cellStyle: {
          paddingRight: 0,
          paddingLeft: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        },
        field: "delete",
        cellRenderer: (params) => {
          return <CloseRounded className="thin" color="error" />;
        },
      },
    ],
    [],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const onRowClicked = useCallback((params) => {
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
      return;
    }
    // dispatch(
    //   sidebarOpen(DASHBOARD_SIDEBAR_TYPES.UPDATE_LOAN_CONDITION, params?.data, {
    //     _id: params.data._id,
    //     conditionId: params.data._id,
    //   }),
    // );
    dispatch(
      sidebarOpen(
        DASHBOARD_SIDEBAR_TYPES.UPDATE_LOAN_CONDITION,
        params?.data?.data,
        {
          loanId: params.data?.data?._id,
          conditionId: params.data._id,
        },
      ),
    );
  }, []);

  const getRowStyle = useCallback((params) => {
    if (params.node.group) {
      return {
        fontWeight: "600",
        backgroundColor: grey[50],
        cursor: "pointer",
        borderBottom: `1px solid ${grey[200]}`,
        // ...renderedRowStyles,
      };
    }
  }, []);

  if (!conditions || conditions.length === 0) {
    return (
      <Flx column center sx={{ p: 4, gap: 2 }}>
        <Txt secondary center>
          No Conditions Found
        </Txt>
      </Flx>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      <Flx g={0.2} column sx={{ mb: 2 }}>
        <Txt bold>Current Open Requirements ({size(conditions)})</Txt>
        <Txt fontSize={12}>
          Review and address outstanding conditions, stips, and document
          requirements to move the loan forward.
        </Txt>
      </Flx>
      <Box
        sx={{
          maxHeight: "800px",
          overflowY: "auto",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          ".ag-row-last": {
            borderBottom: "none !important",
          },
          ".ag-header-viewport": {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <AgGridTableWrapper
          height="100%"
          simpleTable
          suppressMinHeightWhenPopulated={conditions}
        >
          <AgGridReact
            rowData={conditions}
            columnDefs={columnDefs}
            getRowId={getRowId}
            getRowStyle={getRowStyle}
            onRowClicked={onRowClicked}
            groupDisplayType={"groupRows"}
            groupDefaultExpanded={1}
            domLayout="autoHeight"
            tooltipShowDelay={100}
            headerHeight={0}
            enableCellTextSelection
            tooltipMouseTrack
          />
        </AgGridTableWrapper>
      </Box>
    </Box>
  );
};

const CellRendererRequirementDetails = ({ params }) => {
  const { data } = params;

  const conditionType = params?.data?.conditionType;
  const conditionRequirementType = params?.data?.data?.requirementType;
  const description = useMemo(() => {
    const requirementData = data?.data || {};
    if (conditionType === "Term Sheet Approval") {
      return "A Term Sheet was sent requires your approval. Please review the term sheet document, and accept or reject to proceed with your financing request.";
    }
    if (requirementData?.htmlString) {
      return htmlStringToText(requirementData?.htmlString);
    }
  }, [data, conditionType]);

  const conditionLabel = useMemo(() => {
    if (conditionType === "Underwriting Requirement") {
      if (conditionRequirementType === "Document") {
        return "Document Required";
      }
      return conditionRequirementType;
    }
    return conditionType;
  }, [conditionType, conditionRequirementType]);
  return (
    <Flx column>
      <Txt fontSize={12} fontWeight={500}>
        {conditionLabel}
      </Txt>
      <Txt secondary sx={{ fontStyle: "italic", fontSize: "11px" }}>
        {description}
      </Txt>
    </Flx>
  );
};

export default ActiveFinancingRequestsConditionsSection;
